import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import authService from "../services/auth";
import Form from "../components/Form";
import { Eye, EyeOff, X } from "lucide-react";

const Login = () => {
  // Hooks pentru navigare și locație
  const navigate = useNavigate();
  const location = useLocation();
  
  // Context hooks pentru autentificare și notificări
  const { login } = useAuth();
  const { addToast } = useToasts();
  
  // State pentru formular
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const returnTo = searchParams.get('returnTo');
  const lessonId = searchParams.get('lesson');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  // Precompletează emailul din navigare sau localStorage
  useEffect(() => {
    if (location.state && location.state.email) {
      setEmail(location.state.email); // Din navigare (ex: după verificare email)
    } else {
      const verifiedEmail = localStorage.getItem("verifiedEmail");
      if (verifiedEmail) {
        setEmail(verifiedEmail); // Din localStorage
        localStorage.removeItem("verifiedEmail");
      }
    }
    
    // Check if coming from password reset with unverified email
    if (location.state && location.state.fromPasswordReset) {
      if (location.state.email) {
        setEmail(location.state.email);
      }
    }
  }, [location.state]);

  // Gestionează submit-ul formularului
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Încearcă login-ul
    const res = await login(email, password);
    setLoading(false);
    
    if (res.ok) {
      addToast("Logged in", "success-info");
      // Get user info for personalized welcome
      try {
        const userInfo = await authService.getCurrentUser();
        // Redirect based on user status and role
        if (userInfo.user.isActive === false) {
          // Deactivated users (both self and admin) go to profile
          navigate("/profile");
        } else if (userInfo.user.role === "user") {
          navigate("/myspace");
        } else {
          navigate("/");
        }
      } catch {
        // Fallback if user info fetch fails - default to home
        navigate("/");
      }
    } else {
      // Show resend button and message if email not verified
      const isVerificationError = (res.error && typeof res.error === 'string' && res.error.includes("verify")) ||
                                 (Array.isArray(res.error) && res.error.some(err => err.includes("verify")));
      
      if (isVerificationError) {
        setShowResendButton(true);
        addToast("Please verify your email before logging in", "error-warning");
      } else {
        addToast(
          Array.isArray(res.error) ? res.error.join(", ") : res.error,
          "error-warning"
        );
      }
    }
  };

  // Anulează login-ul și merge la home
  const handleCancel = () => {
    if (returnTo === 'home' && lessonId) {
      navigate(`/?lesson=${lessonId}`);
    } else {
      navigate("/");
    }
  };



  return (
    <Form
      title="Login"
      onSubmit={handleSubmit}
      error={null}
      loading={loading}
      submitText="Login"
      cancelText="Cancel"
      onCancel={handleCancel}
      footerLinks={
        <>
          {/* Reserved space for verification link - prevents layout shift */}
          <p className="login-footer-spacer">
            {showResendButton && (
              <Link 
                to="/email-verification-notice" 
                state={{ email }}
              >
                Need verification email?
              </Link>
            )}
          </p>
          <p>
            <Link to="/forgot-password">Forgot Password?</Link>
          </p>
          <p className="login-footer-signup">
            Don't have an account? <Link to={returnTo === 'home' && lessonId ? `/signup?returnTo=home&lesson=${lessonId}` : "/signup"}>Sign up</Link>
          </p>
        </>
      }
    >

      <label htmlFor="email">
        Email
        <div className="login-input-wrapper">
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={`login-input ${email ? "login-input-clear" : ""}`}
          />
          {email && (
            <button
              type="button"
              aria-label="Clear email"
              tabIndex={-1}
              onClick={() => setEmail('')}
              className="login-input-btn login-input-btn-clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </label>
      <label htmlFor="password">
        Password
        <div className="login-input-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={`login-input ${password ? "login-input-actions" : ""}`}
          />
          {password && (
            <>
              <button
                type="button"
                aria-label="Clear password"
                tabIndex={-1}
                onClick={() => setPassword('')}
                className="login-input-btn login-input-btn-clear login-input-btn-clear-password"
              >
                <X size={16} />
              </button>
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword(!showPassword)}
                className="login-input-btn login-input-btn-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </>
          )}
        </div>
      </label>
    </Form>
  );
};

export default Login;
